// CustomerGrowthChart -- yearly new-vs-lost customers, toggleable
// between grouped bar chart and table view. Slots into the Customer
// History page. Data from GET /api/customer-history/growth.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer, LabelList,
} from 'recharts';

const NEW_COLOR = '#059669';   // green
const LOST_COLOR = '#dc2626';  // red

function fmt(n) { return n > 0 ? `+${n}` : String(n); }

export default function CustomerGrowthChart() {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('chart'); // 'chart' | 'table'
  // Drill-down modal state: null when closed, {year, type, sites, loading} when open
  const [drill, setDrill] = useState(null);

  function openDrill(year, type) {
    setDrill({ year, type, sites: [], loading: true });
    axios.get(`/api/customer-history/growth-detail?year=${year}&type=${type}`)
      .then((r) => setDrill({ year, type, sites: r.data.sites || [], loading: false }))
      .catch((e) => setDrill({ year, type, sites: [], loading: false, error: e.message }));
  }

  useEffect(() => {
    axios.get('/api/customer-history/growth')
      .then((r) => { setYears(r.data.years || []); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <div style={{ padding: 16 }}>Loading growth data…</div>;
  if (error)   return <div style={{ padding: 16, color: 'var(--color-bad)' }}>Error: {error}</div>;
  if (years.length === 0) return <div style={{ padding: 16 }}>No growth data yet.</div>;

  const totalNew  = years.reduce((s, y) => s + y.new_count,  0);
  const totalLost = years.reduce((s, y) => s + y.lost_count, 0);
  const netAll    = totalNew - totalLost;

  return (
    <div style={{
      background: 'var(--color-surface, #fff)',
      border: '1px solid var(--color-border, #e5e7eb)',
      borderRadius: 8,
      padding: 'var(--space-4, 16px)',
      marginBottom: 'var(--space-4, 16px)',
    }}>
      {/* Header + toggle */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: 0 }}>Site Growth</h3>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 2 }}>
            {totalNew} installed · {totalLost} lost · <strong>{fmt(netAll)}</strong> net sites all-time
          </div>
        </div>
        <div style={{ display: 'inline-flex', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 4, overflow: 'hidden' }}>
          {['chart', 'table'].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: '4px 12px',
                background: view === v ? 'var(--color-accent, #0e7490)' : 'transparent',
                color: view === v ? '#fff' : 'var(--color-text-dim)',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 12,
                textTransform: 'capitalize',
              }}
            >{v}</button>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        {view === 'chart' ? (
          <div style={{ width: '100%', height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={years} margin={{ top: 20, right: 24, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="year_label" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => [value, name === 'new_count' ? 'Installed' : name === 'lost_count' ? 'Lost' : name]}
                  labelFormatter={(l) => `Year: ${l}`}
                />
                <Legend
                  formatter={(v) => v === 'new_count' ? 'Installed' : v === 'lost_count' ? 'Lost' : v}
                  verticalAlign="top" height={28}
                />
                <Bar dataKey="new_count"  fill={NEW_COLOR}  cursor="pointer"
                     onClick={(d) => openDrill(d.year, 'new')}>
                  <LabelList dataKey="new_count"  position="top" style={{ fontSize: 11, fontWeight: 600 }} />
                </Bar>
                <Bar dataKey="lost_count" fill={LOST_COLOR} cursor="pointer"
                     onClick={(d) => openDrill(d.year, 'lost')}>
                  <LabelList dataKey="lost_count" position="top" style={{ fontSize: 11, fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: 'var(--color-surface-sunken, #f8fafc)' }}>
              <tr>
                <th style={{ padding: '8px 10px', textAlign: 'left'   }}>Year</th>
                <th style={{ padding: '8px 10px', textAlign: 'right'  }}>Installed</th>
                <th style={{ padding: '8px 10px', textAlign: 'right'  }}>Lost</th>
                <th style={{ padding: '8px 10px', textAlign: 'right'  }}>Net</th>
                <th style={{ padding: '8px 10px', textAlign: 'right'  }}>Running Total</th>
              </tr>
            </thead>
            <tbody>
              {years.map((y) => (
                <tr key={y.year} style={{ borderTop: '1px solid var(--color-border, #e5e7eb)' }}>
                  <td style={{ padding: '6px 10px', fontWeight: y.is_ytd ? 700 : 400 }}>{y.year_label}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: NEW_COLOR, fontFamily: 'var(--font-mono)' }}>
                    {y.new_count > 0
                      ? <a onClick={() => openDrill(y.year, 'new')}  style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>{y.new_count}</a>
                      : y.new_count}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', color: LOST_COLOR, fontFamily: 'var(--font-mono)' }}>
                    {y.lost_count > 0
                      ? <a onClick={() => openDrill(y.year, 'lost')} style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>{y.lost_count}</a>
                      : y.lost_count}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: y.net >= 0 ? NEW_COLOR : LOST_COLOR }}>{fmt(y.net)}</td>
                  <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{y.running_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drill && (
        <div
          onClick={() => setDrill(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, maxWidth: 900, width: '100%',
              maxHeight: '85vh', overflow: 'auto', padding: 20,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                <span style={{ color: drill.type === 'new' ? NEW_COLOR : LOST_COLOR }}>
                  {drill.type === 'new' ? 'Installed' : 'Lost'}
                </span>
                {' '}in {drill.year}
                {!drill.loading && <span style={{ color: 'var(--color-text-dim)', fontSize: 14, fontWeight: 400, marginLeft: 8 }}>
                  ({drill.sites.length} site{drill.sites.length === 1 ? '' : 's'})
                </span>}
              </h3>
              <button onClick={() => setDrill(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--color-text-dim)' }}>x</button>
            </div>
            {drill.loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-dim)' }}>Loading...</div>}
            {drill.error && <div style={{ padding: 20, color: LOST_COLOR }}>Error: {drill.error}</div>}
            {!drill.loading && !drill.error && drill.sites.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-dim)' }}>No sites in this bucket.</div>
            )}
            {!drill.loading && drill.sites.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: 'var(--color-surface-sunken, #f8fafc)' }}>
                  <tr>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Site #</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Site Name</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Customer</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Fuel</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>City</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>{drill.type === 'new' ? 'Installed' : 'Last Login'}</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {drill.sites.map((s) => (
                    <tr key={s.site_id} style={{ borderTop: '1px solid var(--color-border, #e5e7eb)' }}>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{s.site_number || '-'}</td>
                      <td style={{ padding: '6px 10px' }}>{s.site_name || '-'}</td>
                      <td style={{ padding: '6px 10px' }}>
                        <Link to={`/customers/${s.customer_id}`} onClick={() => setDrill(null)}>{s.company}</Link>
                      </td>
                      <td style={{ padding: '6px 10px' }}>{s.fuel_brand || '-'}</td>
                      <td style={{ padding: '6px 10px' }}>{[s.city, s.province_state].filter(Boolean).join(', ') || '-'}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                        {s.bucket_date ? String(s.bucket_date).slice(0, 10) : '-'}
                        {drill.type === 'lost' && s.bucket_source === 'status_changed_at' &&
                          <span title="Bucketed by admin-flip date, not last login" style={{ color: 'var(--color-text-dim)', marginLeft: 4 }}>*</span>}
                      </td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {drill.type === 'lost' && drill.sites.some((s) => s.bucket_source === 'status_changed_at') && (
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--color-text-dim)' }}>
                * = year came from the date you marked them Inactive, not from their last login (no login data on file).
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
